import { Link } from 'react-router-dom';
import { useMemo, useState } from "react";
import { ControlCatalogTable } from "../../../components/control-catalog-table";
import { ComplianceJourneyTimeline } from "../../../components/compliance-journey-timeline";
import { CrossFrameworkMapping } from "../../../components/cross-framework-mapping";
import { ScrollReveal } from "../../../components/scroll-reveal";
import { TechMappingVisual } from "../../../components/tech-mapping-visual";
import { RBI_CONTROLS, RBI_DOMAINS } from "../../../data/rbi-controls";
import { cn } from "../../../lib/utils";

function toRow(c) {
  return {
    controlId: c.controlId,
    controlDomain: c.controlDomain,
    controlRequirement: c.controlRequirement,
    minimumImplementationStandard: c.minimumImplementationStandard,
    evidenceArtifacts: c.evidenceArtifacts,
    owner: c.owner,
    frequency: c.frequency,
    technologyEnablers: c.technologyEnablers,
    auditTestMethod: c.auditTestMethod,
    commonGaps: c.commonGaps,
    severity: c.severity,
    automationPotential: c.automationPotential,
  };
}

export default function RbiFramework() {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const rows = useMemo(() => RBI_CONTROLS.map(toRow), []);
  const mappingFlows = useMemo(
    () => [
      {
        domain: "IAM & PAM",
        control: "MFA Implementation",
        category: "Identity Provider",
        vendors: ["RSA", "Opentext NetIQ", "CyberArk", "1Kosmos", "Yubico", "e-Mudra", "Entrust IDAAS"],
      },
      {
        domain: "IAM & PAM",
        control: "Privileged Access",
        category: "PAM Solution",
        vendors: ["CyberArk", "Arcon"],
      },
      {
        domain: "SOC & Logging",
        control: "Log Collection",
        category: "SIEM",
        vendors: ["Splunk", "Google Chronicle", "Innspark", "Gurucul", "Securonix", "Opentext Arcsight", "Sumologic"],
      },
      {
        domain: "Network Security",
        control: "Perimeter Protection",
        category: "NGFW",
        vendors: ["Check-Point", "Forcepoint"],
      },
      {
        domain: "Endpoint Security",
        control: "EDR",
        category: "Endpoint Detection",
        vendors: ["SentinelOne","Check-Point"],
      },
      {
        domain: "Data Security",
        control: "Encryption",
        category: "HSM / KMS",
        vendors: ["Thales", "Entrust", "Utimaco"],
      },
    ],
    [],
  );

  const crossRows = useMemo(
    () => [
      {
        controlArea: "Board Oversight",
        rbi: "RBI-GOV-01",
        sebi: "CSCRF-GOV-01",
        irdai: "IRDAI-GOV-01",
        certIn: "-",
      },
      {
        controlArea: "CISO Designation",
        rbi: "RBI-GOV-02",
        sebi: "CSCRF-GOV-02",
        irdai: "IRDAI-GOV-02",
        certIn: "-",
      },
      {
        controlArea: "Risk Assessment",
        rbi: "RBI-RISK-01",
        sebi: "CSCRF-RISK-01",
        irdai: "IRDAI-RISK-01",
        certIn: "-",
      },
      {
        controlArea: "Access Control",
        rbi: "RBI-IAM-04",
        sebi: "CSCRF-IAM-01",
        irdai: "IRDAI-IAM-01",
        certIn: "-",
      },
      {
        controlArea: "PAM",
        rbi: "RBI-IAM-03",
        sebi: "CSCRF-IAM-02",
        irdai: "-",
        certIn: "-",
      },
      {
        controlArea: "SIEM/SOC",
        rbi: "RBI-SOC-01",
        sebi: "CSCRF-SEC-01",
        irdai: "IRDAI-SOC-01",
        certIn: "CERT-15",
      },
      {
        controlArea: "Log Retention",
        rbi: "RBI-SOC-03",
        sebi: "-",
        irdai: "-",
        certIn: "CERT-15, CERT-16",
      },
      {
        controlArea: "Incident Reporting",
        rbi: "RBI-INC-03",
        sebi: "CSCRF-INC-02",
        irdai: "IRDAI-INC-01",
        certIn: "CERT-01 to CERT-14",
      },
      {
        controlArea: "Vendor Risk",
        rbi: "RBI-TPR-01",
        sebi: "CSCRF-TPR-01",
        irdai: "IRDAI-VEN-01",
        certIn: "-",
      },
      {
        controlArea: "BCP/DR",
        rbi: "RBI-BCP-01",
        sebi: "CSCRF-RES-01",
        irdai: "IRDAI-BCP-01",
        certIn: "-",
      },
    ],
    [],
  );

  return (
    <div>
      <section className="relative overflow-hidden py-16" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Indian Regulatory Frameworks / RBI
          </div>

          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-[-0.03em] text-text-primary md:text-6xl">
            RBI IT Governance &
            <br />
            Cybersecurity Framework
          </h1>

          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-text-secondary">
            Comprehensive compliance architecture for banks, NBFCs, payment aggregators,
            and regulated financial entities under RBI supervision.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#control-catalog"
              className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Download Control Catalog
            </a>
            <Link
              to="/assessment"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              Book Assessment
            </Link>
            <Link
              to="/technology-mapping"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl px-5 text-sm font-semibold text-[color:var(--text-link)] transition-colors hover:text-text-primary"
            >
              View Technology Map →
            </Link>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="glass-card sheen p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Domains
              </div>
              <div className="mt-2 font-[family:var(--font-space-grotesk)] text-3xl font-semibold text-text-primary">
                15
              </div>
            </div>
            <div className="glass-card sheen p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Controls
              </div>
              <div className="mt-2 font-[family:var(--font-space-grotesk)] text-3xl font-semibold text-text-primary">
                127
              </div>
            </div>
            <div className="glass-card sheen p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Updated
              </div>
              <div className="mt-2 font-[family:var(--font-space-grotesk)] text-3xl font-semibold text-text-primary">
                2024
              </div>
            </div>
            <div className="glass-card sheen p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Automation
              </div>
              <div className="mt-2 font-[family:var(--font-space-grotesk)] text-3xl font-semibold text-text-primary">
                78%
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20">
        <div className="container-ivalue">
          <div className="grid gap-10 lg:grid-cols-2">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                  Executive overview
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-text-secondary">
                  The Reserve Bank of India mandates comprehensive IT governance and
                  cybersecurity controls for all regulated entities. These directions aim
                  to ensure cyber resilience across India's financial ecosystem.
                </p>

                <div className="mt-6 space-y-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Key objectives
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {[
                        "Cyber resilience across digital channels",
                        "Fraud prevention and transaction security",
                        "IT risk governance at board level",
                        "SOC-driven continuous monitoring",
                        "Third-party and outsourcing risk management",
                      ].map((t) => (
                        <div
                          key={t}
                          className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary"
                        >
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Applicable to
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {[
                        "Scheduled Commercial Banks",
                        "Non-Banking Financial Companies (NBFCs)",
                        "Payment Aggregators & Payment Gateways",
                        "Credit Information Companies",
                        "Account Aggregators",
                      ].map((t) => (
                        <div
                          key={t}
                          className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary"
                        >
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Key statistics
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    { k: "Control domains", v: "15" },
                    { k: "Individual controls", v: "127" },
                    { k: "Automation potential", v: "78%" },
                    { k: "Typical implementation", v: "12 weeks" },
                  ].map((s) => (
                    <div
                      key={s.k}
                      className="rounded-2xl border border-border-subtle bg-white/5 p-4"
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                        {s.k}
                      </div>
                      <div className="mt-2 font-[family:var(--font-space-grotesk)] text-2xl font-semibold text-text-primary">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Regulatory timeline
                  </div>
                  <div className="mt-3 space-y-2">
                    {[
                      { y: "2019", t: "Initial IT Framework" },
                      { y: "2021", t: "Cyber Security Guidelines" },
                      { y: "2023", t: "Master Directions Update" },
                      { y: "2024", t: "Enhanced Reporting Requirements" },
                    ].map((e) => (
                      <div
                        key={e.y}
                        className="flex items-center justify-between rounded-xl border border-border-subtle bg-white/5 px-4 py-3"
                      >
                        <div className="font-mono text-xs font-semibold text-text-secondary">
                          {e.y}
                        </div>
                        <div className="text-sm font-semibold text-text-primary">
                          {e.t}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20">
        <div className="container-ivalue">
          <ScrollReveal>
            <TechMappingVisual flows={mappingFlows} />
          </ScrollReveal>

          <div className="mt-10">
            <ScrollReveal>
              <ComplianceJourneyTimeline />
            </ScrollReveal>
          </div>

          <div className="mt-10">
            <ScrollReveal>
              <CrossFrameworkMapping rows={crossRows} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20">
        <div className="container-ivalue">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              Control domain navigator
            </h2>
          </ScrollReveal>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {RBI_DOMAINS.map((d) => {
              const isActive = selectedDomain === d.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  className={cn(
                    "glass-card sheen p-4 text-left",
                    isActive && "border-[rgba(59,130,246,0.45)]",
                  )}
                  onClick={() => {
                    const next = isActive ? null : d.key;
                    setSelectedDomain(next);
                    document.getElementById("control-catalog")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                >
                  <div className="text-sm font-semibold text-text-primary">
                    {d.title}
                  </div>
                  <div className="mt-1 text-sm text-text-secondary">
                    {d.controlCount} ctrl
                  </div>
                  <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Click to filter
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="control-catalog">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                  RBI control catalog
                </h2>
                <p className="mt-3 max-w-3xl text-lg text-text-secondary">
                  The table below includes Domains 1–15 from this prompt.
                </p>
              </div>

              {selectedDomain && (
                <button
                  type="button"
                  className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
                  onClick={() => setSelectedDomain(null)}
                >
                  Clear domain filter
                </button>
              )}
            </div>
          </ScrollReveal>

          <div className="mt-10">
            <ControlCatalogTable
              data={rows}
              initialDomain={selectedDomain ?? undefined}
              onDomainChange={(d) => setSelectedDomain(d)}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
